// ABOUTME: Captures stable candidate identities for navigation in an open Kanban modal.
// ABOUTME: Maps snapshot destinations back to the current column for safe scrolling.
const getApplicantId = (applicant: Compleo.IObject) => {
    const pk = applicant?.pk;
    return typeof pk === "string" ? pk.split(":").pop() : undefined;
};

export interface ApplicantNavigationEntry {
    applicantId?: string;
    virtualIndex: number;
}

export interface ApplicantNavigationState {
    currentPosition: number;
    pendingPosition: number | null;
}

interface ApplicantNavigationOrder {
    applicantPK: string;
    order: number;
    stageId: string;
}

export const createApplicantNavigationVersion = (
    orderData: ApplicantNavigationOrder[],
    stageId: string
) =>
    JSON.stringify(
        orderData
            .filter((item) => item.stageId === stageId)
            .map((item) => [item.applicantPK, item.order])
    );

export const getLoadedApplicantId = (
    snapshotApplicant: ApplicantNavigationEntry | undefined,
    applicants: Compleo.IObject[],
    canHydrate = true
) => {
    if (!canHydrate || !snapshotApplicant || snapshotApplicant.applicantId) {
        return undefined;
    }

    return getApplicantId(applicants[snapshotApplicant.virtualIndex]);
};

export const createApplicantNavigationSnapshot = (
    applicants: Compleo.IObject[],
    selectedIndex: number
) => {
    const selectedApplicantId = getApplicantId(applicants[selectedIndex]);
    const snapshotApplicants = applicants.map((applicant, virtualIndex) => {
        const applicantId = getApplicantId(applicant);
        return {
            ...(applicantId ? { applicantId } : {}),
            virtualIndex
        };
    });

    return {
        applicants: snapshotApplicants,
        initialPosition: selectedApplicantId
            ? snapshotApplicants.findIndex(
                  (applicant) => applicant.applicantId === selectedApplicantId
              )
            : -1
    };
};

export const hydrateApplicantNavigationSnapshot = (
    snapshotApplicants: ApplicantNavigationEntry[],
    applicants: Compleo.IObject[],
    canHydrate = true
) => {
    if (!canHydrate) {
        return snapshotApplicants;
    }

    let changed = false;
    const hydratedApplicants = snapshotApplicants.map((snapshotApplicant) => {
        if (snapshotApplicant.applicantId) {
            return snapshotApplicant;
        }

        const applicantId = getLoadedApplicantId(snapshotApplicant, applicants);
        if (!applicantId) {
            return snapshotApplicant;
        }

        changed = true;
        return { ...snapshotApplicant, applicantId };
    });

    return changed ? hydratedApplicants : snapshotApplicants;
};

export const findApplicantNavigationPosition = (
    snapshotApplicants: ApplicantNavigationEntry[],
    currentPosition: number,
    direction: -1 | 1,
    includeUnresolved: boolean
) => {
    for (
        let position = currentPosition + direction;
        position >= 0 && position < snapshotApplicants.length;
        position += direction
    ) {
        if (includeUnresolved || snapshotApplicants[position].applicantId) {
            return position;
        }
    }

    return -1;
};

export const resolveApplicantNavigationState = (
    state: ApplicantNavigationState,
    applicants: ApplicantNavigationEntry[],
    targetPosition: number
): ApplicantNavigationState => {
    if (applicants[targetPosition]?.applicantId) {
        return {
            currentPosition: targetPosition,
            pendingPosition: null
        };
    }

    return {
        currentPosition: state.currentPosition,
        pendingPosition: targetPosition
    };
};
